// Frontera ÚNICA con la app de escritorio (el cascarón de Electron que carga
// camp.beemktg.com en vivo). El preload inyecta window.desktop con contextBridge;
// aquí solo se declara el contrato y se leen capacidades.
//
// POR QUÉ UN OBJETO DE CAPACIDADES Y NO EL USER AGENT: el user agent de Electron
// se puede cambiar, lo copian otros embebidos y no dice NADA de lo que la carcasa
// sabe hacer. `window.desktop` existe si y solo si el preload corrió, y cada
// función que trae es una capacidad real. Preguntar "¿puedo notificar?" en vez de
// "¿quién eres?" también deja que fase 3 encienda la grabación sin tocar la web.
//
// Este módulo NO importa nada del resto de la app a propósito: lo consumen
// install.ts, push.ts, AuthContext y el chrome, y una dependencia cruzada aquí
// crearía ciclos. La única excepción es lib/dominios.ts, que es una hoja (solo
// constantes, sin imports): así los textos del login salen de la misma lista.

import { SOLO_CUENTAS_BEE, USA_CUENTA_BEE } from './dominios'

export type PlataformaEscritorio = 'darwin' | 'win32' | 'linux'

/** Lo que devuelve desktop.login(): id_token DE GOOGLE, no un token de Firebase. */
export interface LoginEscritorio {
  idToken: string
  email: string
}

/** Códigos con los que el cascarón rechaza login(). */
export type CodigoLoginEscritorio =
  | 'sin_cliente'
  | 'cancelado'
  | 'tiempo_agotado'
  | 'dominio'
  | 'error'

export interface RechazoLoginEscritorio {
  code: CodigoLoginEscritorio
  message: string
}

export interface NotifEscritorio {
  id: string
  title: string
  body: string
  /** Ruta RELATIVA de camp; siempre empieza con '/'. */
  link?: string
}

export interface EventoActualizacion {
  state: 'disponible' | 'descargada' | 'error'
  version?: string
}

/** Qué tan usable está una IA o una app en esta Mac. «Instalado» no es «usable». */
export type EstadoCapacidadMac = 'listo' | 'sin_sesion' | 'sin_permiso' | 'no_instalado' | 'no_verificado'

export interface CapacidadMac {
  estado: EstadoCapacidadMac
  /** Solo la versión; nunca una ruta. */
  version: string | null
  cuentaBee?: boolean | null
  plan?: string | null
}

export type NombreCapacidadMac = 'claude' | 'chatgpt' | 'blender' | 'rhino' | 'photoshop' | 'illustrator'
export type CapacidadesMac = Partial<Record<NombreCapacidadMac, CapacidadMac>>

export type FaseAgenteMac =
  | 'apagado'
  | 'sin_llavero'
  | 'sin_enlazar'
  | 'esperando_confirmacion'
  | 'enlazado'
  | 'revocado'

/** Cómo está el agente de esta Mac. Sin rutas, sin tokens, sin la llave. */
export interface EstadoAgenteMac {
  fase: FaseAgenteMac
  agId: string | null
  /** Mientras espera confirmación: el código y la ruta RELATIVA de camp donde se confirma. */
  enlace: { codigo: string; url: string; expiraEn: string } | null
  conectado: boolean
  ultimoLatido: string | null
  capacidades: CapacidadesMac | null
  detectadoEn: string | null
  iaPreferida: 'claude' | 'chatgpt' | null
  mensaje: string | null
}

/**
 * El agente local (IA en esta Mac). La web no elige llave, servidor ni ejecutable.
 * El código opcional de acceso viaja solo al CLI oficial de esta Mac.
 * Rechaza con { code, message }.
 */
export interface AgenteEscritorio {
  estado(): Promise<EstadoAgenteMac>
  /** Pide el enlace; la persona lo confirma en `url` con su sesión. */
  enlazar(): Promise<{ codigo: string; url: string; expiraEn: string }>
  /** Detección fresca de Claude, ChatGPT, Blender, Rhino y Adobe. */
  capacidades(): Promise<CapacidadesMac>
  /** Ausentes en versiones anteriores: acceso oficial sin Terminal ni tokens en la web. */
  conectarClaude?(): Promise<void>
  codigoClaude?(codigo: string): Promise<void>
  cancelarConexionClaude?(): Promise<void>
}

export interface EnlaceAsistente { etiqueta: string; ruta: string }

export interface AyudaEscritorio {
  readonly version?: 2 | 3
  preguntar(consulta: { token: string; mensajes: Array<{ rol: 'persona' | 'asistente'; texto: string }>; destino?: { pid?: string; poId?: string } }): Promise<{ ia: 'claude' | 'chatgpt'; texto: string; enlaces?: EnlaceAsistente[]; propuesta?: { id: string; resumen: string; destino: string; audiencia: string; titulo?: string; texto: string }; cambio?: { pid: string; poId?: string } }>
  confirmar?(entrada: { token: string; propuestaId: string }): ReturnType<AyudaEscritorio['preguntar']>
  cancelar(): Promise<void>
}

export interface CampDesktop {
  /** Siempre presente. Sirve para distinguir escritorio de navegador sin mirar el user agent. */
  readonly version: string
  readonly platform: PlataformaEscritorio
  readonly canRecordSystemAudio: false
  /**
   * Abre el navegador del sistema para entrar con Google y resuelve con el
   * id_token de Google. La web hace signInWithCredential.
   * Rechaza con { code, message }.
   */
  login(): Promise<LoginEscritorio>
  /** Notificación del sistema. Al hacer clic la app se enfoca y navega a 'link'. */
  notify(n: NotifEscritorio): Promise<void>
  /** Abre una URL http(s) en el navegador del sistema. Todo lo demás se rechaza. */
  openExternal(url: string): Promise<void>
  /** Insignia en el dock/barra (número de no leídos; 0 la quita). */
  setBadge(count: number): Promise<void>
  /** La app pide navegar (clic en notificación, deep link). Devuelve la desuscripción. */
  onNavigate(cb: (link: string) => void): () => void
  /** La ventana se va a cerrar/ocultar: volcar cache y borradores YA. */
  onBeforeHide(cb: () => void): () => void
  /** Estado del auto-update para mostrarlo discretamente. */
  onUpdate(cb: (e: EventoActualizacion) => void): () => void
  /** Ausente en apps anteriores al agente (Ola 2): la web debe preguntar si existe. */
  readonly ayuda?: AyudaEscritorio
  readonly agente?: AgenteEscritorio
  restartToUpdate(): Promise<void>
}

declare global {
  interface Window {
    desktop?: CampDesktop
  }
}

/** El objeto de capacidades, o null si esto es un navegador. */
export function escritorio(): CampDesktop | null {
  if (typeof window === 'undefined') return null
  return window.desktop ?? null
}

/** ¿Corremos dentro de la app de escritorio? */
export function esEscritorio(): boolean {
  return Boolean(escritorio())
}

/** Versión del cascarón (para el "acerca de" y la telemetría). null en navegador. */
export function versionEscritorio(): string | null {
  return escritorio()?.version ?? null
}

/**
 * ¿`link` es una ruta INTERNA de camp que react-router puede navegar?
 *
 * El '//' se rechaza aparte y no por gusto: '//evil.com' empieza con '/' pero es
 * una URL protocol-relative, así que navigate() la trataría como enlace externo.
 * Es la vía clásica de escaparse de una validación "empieza con barra".
 */
export function esRutaInterna(link: unknown): link is string {
  if (typeof link !== 'string') return false
  if (!link.startsWith('/') || link.startsWith('//')) return false
  // Un '\' tras la barra lo normalizan algunos navegadores a '/', mismo escape.
  if (link.startsWith('/\\')) return false
  return true
}

/** Normaliza el rechazo de desktop.login() a uno de los códigos del contrato. */
export function codigoLoginEscritorio(err: unknown): CodigoLoginEscritorio {
  const code = (err as { code?: unknown } | null)?.code
  if (
    code === 'sin_cliente' ||
    code === 'cancelado' ||
    code === 'tiempo_agotado' ||
    code === 'dominio'
  ) {
    return code
  }
  return 'error'
}

/**
 * Mensaje en español para la pantalla de bienvenida. Se escribe pensando en la
 * persona que lo va a leer, no en el error: nadie sabe qué es un cliente OAuth,
 * pero sí entiende "entra desde el navegador mientras tanto".
 *
 * Cada código dice UNA cosa distinta y se traduce como tal. Meter 'cancelado' y
 * 'tiempo_agotado' en el mismo saco que 'dominio' —como estaba— le contaba a la
 * persona que su cuenta no es de bee cuando lo único que hizo fue cerrar una
 * pestaña, y la mandaba a pedir ayuda por el problema equivocado. En el navegador
 * ese mismo caso ya decía la verdad ("Cerraste la ventana antes de entrar."), así
 * que el escritorio no puede decir menos.
 */
export function mensajeLoginEscritorio(code: CodigoLoginEscritorio): string {
  switch (code) {
    case 'sin_cliente':
      return 'La app de escritorio no tiene configurado el acceso con Google; entra desde el navegador mientras tanto'
    case 'cancelado':
      return 'Cerraste la ventana antes de entrar.'
    case 'tiempo_agotado':
      return 'Tardó demasiado; vuelve a intentarlo.'
    case 'dominio':
      return SOLO_CUENTAS_BEE
    case 'error':
      return USA_CUENTA_BEE
  }
}

/**
 * Marca el <html> cuando corremos dentro del cascarón, con la plataforma.
 *
 * POR QUÉ HACE FALTA: en Mac la ventana usa titleBarStyle 'hiddenInset', o sea que
 * el contenido web empieza en y=0 y los tres semáforos flotan encima — justo sobre
 * el logo de la barra de camp. La web es la única que puede reservar ese hueco (el
 * main no sabe dónde está el logo) y, ya que la barra de título nativa no existe,
 * también es la única que puede declarar por dónde se arrastra la ventana. Todo eso
 * vive en index.css bajo html[data-escritorio='darwin']; aquí solo se pone la marca.
 *
 * Es un atributo en el <html> y no una clase en el body a propósito: está puesto
 * antes del primer render de React, así que no hay un fotograma con el logo tapado.
 */
export function marcarEscritorio(): void {
  const d = escritorio()
  if (!d || typeof document === 'undefined') return
  document.documentElement.dataset.escritorio = d.platform
}
